import { ThunkAction } from 'redux-thunk'
import { RootState } from 'src/reducers'
import {
  CREATE_ONBOARDING_WORKFLOW,
  GET_ONBOARDING_WORKFLOW,
  SHOW_ERROR,
  UPDATE_ONBOARDING_WORKFLOW,
} from '.'
import * as authApi from '../api/auth'
import * as workflowApi from '../api/workflow'
import { TemplateResponseData } from '../components/organisms/workflow-template/types'
import { WORKFLOW_ERROR_TYPES } from '../components/organisms/workflow-template/WorkflowError'
import {
  trackWorkflowStepSubmitFailure,
  trackWorkflowStepSubmitSuccess,
  WorkflowTrackingProps,
} from '../events'
import { VGSForm } from '../hooks/useVGS'
import { SupportedCountryCode } from '../models/types'

type Action<T = any, P = any> = {
  type: T
  payload?: P
}

export type UpdateOnboardingOptions = Partial<{
  form?: VGSForm
  login?: boolean
}>

const STEPS_COMPLETE_ID = '00000000-0000-0000-0000-000000000000'

const getCurrentWorkflow = (onboarding: workflowApi.Onboarding) => {
  // TODO(AUSL-249): Remove replaceAll after BE updates template
  const template = onboarding.workflow.template.value.replace(
    /jsonData/g,
    'json_data'
  )

  return {
    template: JSON.parse(template),
    currentStep: onboarding.workflow.currentStepId.value,
    currentStepStatus: onboarding.workflow.currentStepStatus,
  }
}

export const createOnboardingRequest = (
  data: workflowApi.CreateOnboardingRequest
): ThunkAction<void, RootState, null, Action> => {
  return async dispatch => {
    try {
      dispatch({ type: CREATE_ONBOARDING_WORKFLOW.IN_PROGRESS })
      const response = await workflowApi.createOnboarding(data)
      const currentWorkflow = getCurrentWorkflow(response.onboarding)

      dispatch({
        type: CREATE_ONBOARDING_WORKFLOW.SUCCESS,
        payload: currentWorkflow,
      })

      return { uniqueLink: response.personLocator.id.value }
      // TODO add tracking event for create onboarding
    } catch (error) {
      dispatch({
        type: CREATE_ONBOARDING_WORKFLOW.FAIL,
        payload: 'Error on create onboarding',
      })
      dispatch({ error, type: SHOW_ERROR })
      throw error
    }
  }
}

export const updateOnboardingRequest = (
  uniqueLink: string,
  data: TemplateResponseData,
  currentPageTemplate,
  trackingData: WorkflowTrackingProps,
  { form }: UpdateOnboardingOptions
) => {
  return async dispatch => {
    try {
      let response
      if (form) {
        response = await workflowApi.updateOnboardingViaVGS(
          uniqueLink,
          data,
          currentPageTemplate,
          form
        )
      } else {
        dispatch({ type: UPDATE_ONBOARDING_WORKFLOW.IN_PROGRESS })
        response = await workflowApi.updateOnboarding(
          uniqueLink,
          data,
          currentPageTemplate
        )
      }

      dispatch(trackWorkflowStepSubmitSuccess(trackingData))

      if (response.workflow.currentStepId.value === STEPS_COMPLETE_ID) {
        dispatch({
          type: UPDATE_ONBOARDING_WORKFLOW.SUCCESS,
          payload: {
            stepsComplete: true,
          },
        })
        return
      }

      const currentWorkflow = getCurrentWorkflow(response)

      dispatch({
        type: UPDATE_ONBOARDING_WORKFLOW.SUCCESS,
        payload: {
          ...currentWorkflow,
          error: null,
          stepsComplete: false,
        },
      })
    } catch (error) {
      dispatch({
        type: UPDATE_ONBOARDING_WORKFLOW.FAIL,
        payload: {
          error: 'Error on update onboarding',
          type: form
            ? WORKFLOW_ERROR_TYPES.DOC_UPLOAD
            : WORKFLOW_ERROR_TYPES.GENERIC,
        },
      })
      dispatch(
        trackWorkflowStepSubmitFailure({
          ...trackingData,
          errorMessage: error.message,
          errorCode: error.code,
        })
      )
    }
  }
}

export const getOnboardingRequest = (
  uniqueLink: string,
  countryCode: SupportedCountryCode
) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: GET_ONBOARDING_WORKFLOW.IN_PROGRESS })

      // Prevent dasher applications from being tied
      // to an already logged in user when going through
      // a new dasher onboarding flow
      await authApi.logoutPreExistingUser(
        getState().consumerEmail,
        getState().application.email
      )

      const response = await workflowApi.getOnboarding(uniqueLink, countryCode)

      // Currently BE will send {} if no steps remain
      if (response.workflow.currentStepId.value === STEPS_COMPLETE_ID) {
        dispatch({
          type: UPDATE_ONBOARDING_WORKFLOW.SUCCESS,
          payload: {
            stepsComplete: true,
          },
        })
        return
      }

      const currentWorkflow = getCurrentWorkflow(response)

      dispatch({
        type: GET_ONBOARDING_WORKFLOW.SUCCESS,
        payload: {
          ...currentWorkflow,
          error: null,
          stepsComplete: false,
        },
      })
    } catch (error) {
      dispatch({
        type: GET_ONBOARDING_WORKFLOW.FAIL,
        payload: 'Error on get onboarding',
      })
    }
  }
}
